import { useQuery } from '@tanstack/react-query'
import { tasksApi } from '@/lib/api'
import { TASK_ENTITY_PAGE_SIZE, type TaskEntityTab } from '@/types'

interface EntityQueryParams {
  taskUuid: string | undefined
  tab: TaskEntityTab
  activeTab: TaskEntityTab
  page: number
  search: string
}

function useTaskEntityQuery<T>({
  taskUuid,
  tab,
  activeTab,
  page,
  search,
  queryKey,
  fetcher,
}: EntityQueryParams & {
  queryKey: string
  fetcher: (uuid: string, offset: number, q: string) => Promise<{ data: T }>
}) {
  const offset = (page - 1) * TASK_ENTITY_PAGE_SIZE
  const enabled = !!taskUuid && tab === activeTab

  return useQuery({
    queryKey: [queryKey, taskUuid, page, search],
    queryFn: async () => (await fetcher(taskUuid!, offset, search)).data,
    enabled,
    placeholderData: (prev) => prev,
  })
}

export function useTaskKMCodes(params: EntityQueryParams) {
  return useTaskEntityQuery({
    ...params,
    queryKey: 'task-km-codes',
    fetcher: (uuid, offset, q) =>
      tasksApi.getKMCodes(uuid, { limit: TASK_ENTITY_PAGE_SIZE, offset, q: q || undefined }),
  })
}

export function useTaskBoxes(params: EntityQueryParams) {
  return useTaskEntityQuery({
    ...params,
    queryKey: 'task-boxes',
    fetcher: (uuid, offset, q) =>
      tasksApi.getBoxes(uuid, { limit: TASK_ENTITY_PAGE_SIZE, offset, q: q || undefined }),
  })
}

export function useTaskPallets(params: EntityQueryParams) {
  return useTaskEntityQuery({
    ...params,
    queryKey: 'task-pallets',
    fetcher: (uuid, offset, q) =>
      tasksApi.getPallets(uuid, { limit: TASK_ENTITY_PAGE_SIZE, offset, q: q || undefined }),
  })
}
